import React, { useEffect, useMemo, useState } from "react";
import { fetchHomepageConfig, readHomepageConfigCacheSync } from "@/lib/homepageConfigCache";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

/**
 * Two independent side ads (left/right) fixed at desktop screens (≥1024px).
 * Each ad renders a PNG image on top of an optional header line and a
 * clickable button whose colors, size, radius and shadow are fully editable
 * from /admin/publicidade. Animations respect prefers-reduced-motion.
 *
 * Timing model:
 *   - `delay_ms`   → wait N ms before first appearance.
 *   - `cycle_ms`   → total period of the on/off cycle. 0 means always visible.
 *   - `visible_ms` → for how long the ad is shown per cycle
 *                    (must be < cycle_ms; the difference is the hidden gap).
 * We schedule with self-restarting setTimeout (never setInterval) with a
 * 500ms floor to tolerate garbage/tiny values entered in the admin.
 */
export default function SideAds() {
  const { lang } = useI18n();
  // Sync cache read → zero flash on first paint if we already have a cached copy.
  const [cfg, setCfg] = useState(() => readHomepageConfigCacheSync() || {});
  useEffect(() => {
    let mounted = true;
    // Prime with any cached data (fast) and then ALWAYS revalidate against
    // the network so recently-saved admin edits (e.g. new `ads` field) reach
    // the client on the very next mount — bypassing the stale
    // fetchHomepageConfig() module cache.
    fetchHomepageConfig().then((d) => { if (mounted && d) setCfg(d); }).catch(() => {});
    api.get("/homepage-config")
      .then((r) => { if (mounted && r?.data) setCfg(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const ads = cfg?.ads;
  if (!ads?.enabled) return null;

  return (
    <>
      {/* Keyframes injected once so any <SideAd> can just reference `animation:` */}
      <style>{`
        @keyframes eudAdFloat     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes eudAdPulse     { 0%,100%{transform:scale(1)} 50%{transform:scale(1.045)} }
        @keyframes eudAdBounce    { 0%,20%,50%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-10px)} 60%{transform:translateY(-6px)} }
        @keyframes eudAdShake     { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-3px)} 40%,80%{transform:translateX(3px)} }
        @keyframes eudAdBlink     { 0%,100%{opacity:1} 50%{opacity:0.55} }
        @keyframes eudAdSlideY    { 0%{transform:translateY(0)} 50%{transform:translateY(12px)} 100%{transform:translateY(0)} }
        @keyframes eudAdGlow      { 0%,100%{filter:drop-shadow(0 0 0 rgba(255,235,59,0))} 50%{filter:drop-shadow(0 0 18px rgba(255,235,59,.65))} }
        @keyframes eudAdWobble    { 0%,100%{transform:rotate(0)} 25%{transform:rotate(-5deg)} 75%{transform:rotate(5deg)} }
        @keyframes eudAdSwing     { 0%,100%{transform:rotate(0)} 20%{transform:rotate(15deg)} 40%{transform:rotate(-10deg)} 60%{transform:rotate(5deg)} 80%{transform:rotate(-5deg)} }
        @keyframes eudAdHeartbeat { 0%,100%{transform:scale(1)} 14%{transform:scale(1.15)} 28%{transform:scale(1)} 42%{transform:scale(1.15)} 70%{transform:scale(1)} }
        @keyframes eudAdFlip      { 0%{transform:perspective(400px) rotateY(0)} 100%{transform:perspective(400px) rotateY(360deg)} }
        @keyframes eudAdTilt      { 0%,100%{transform:rotate(-3deg)} 50%{transform:rotate(3deg)} }
        @keyframes eudAdZoom      { 0%,100%{transform:scale(1)} 50%{transform:scale(1.12)} }
        @keyframes eudAdSpin      { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
        @keyframes eudAdRubber    { 0%,100%{transform:scale(1,1)} 30%{transform:scale(1.25,0.75)} 40%{transform:scale(0.75,1.25)} 50%{transform:scale(1.15,0.85)} 65%{transform:scale(0.95,1.05)} 75%{transform:scale(1.05,0.95)} }
        @keyframes eudAdJello     { 0%,11.1%,100%{transform:none} 22.2%{transform:skewX(-12.5deg) skewY(-12.5deg)} 33.3%{transform:skewX(6.25deg) skewY(6.25deg)} 44.4%{transform:skewX(-3.125deg) skewY(-3.125deg)} 55.5%{transform:skewX(1.5deg) skewY(1.5deg)} }
        @media (prefers-reduced-motion: reduce) {
          .eud-side-ad, .eud-side-ad * { animation: none !important; }
        }
      `}</style>
      <SideAd side="left" ad={ads.left} lang={lang} />
      <SideAd side="right" ad={ads.right} lang={lang} />
    </>
  );
}

const ANIM_MAP = {
  none:      "",
  float:     "eudAdFloat 3.4s ease-in-out infinite",
  pulse:     "eudAdPulse 2.2s ease-in-out infinite",
  bounce:    "eudAdBounce 2.4s ease-in-out infinite",
  shake:     "eudAdShake 0.6s ease-in-out infinite",
  blink:     "eudAdBlink 1.2s ease-in-out infinite",
  slideY:    "eudAdSlideY 3.2s ease-in-out infinite",
  glow:      "eudAdGlow 2.4s ease-in-out infinite",
  wobble:    "eudAdWobble 2.6s ease-in-out infinite",
  swing:     "eudAdSwing 3.0s ease-in-out infinite",
  heartbeat: "eudAdHeartbeat 1.4s ease-in-out infinite",
  flip:      "eudAdFlip 3.0s ease-in-out infinite",
  tilt:      "eudAdTilt 2.0s ease-in-out infinite",
  zoom:      "eudAdZoom 2.0s ease-in-out infinite",
  spin:      "eudAdSpin 6.0s linear infinite",
  rubber:    "eudAdRubber 1.8s ease-in-out infinite",
  jello:     "eudAdJello 2.4s ease-in-out infinite",
};

const SIZE_CLASS = {
  sm: "px-2.5 py-1 text-[10px]",
  md: "px-3.5 py-1.5 text-xs",
  lg: "px-5 py-2.5 text-sm",
};

function SideAd({ side, ad, lang }) {
  // Strict per-language: an ad only appears in the current language if THAT
  // language has its own image. Empty EN/ES → no ad rendered in those
  // languages (even if PT has content). PT still supports the legacy shared
  // `image_url` for backwards-compat with configs saved before the
  // per-language fields existed.
  const langImage = (ad?.[`image_url_${lang}`] || "").trim();
  const langButtonUrl = (ad?.[`button_url_${lang}`] || "").trim();
  const imageUrl = langImage || (lang === "pt" ? (ad?.image_url || "").trim() : "");
  // Button URL falls back to the PT-specific url or the legacy `button_url`
  // if the current language doesn't have its own — the link works cross-
  // language (unlike the image, which is visual content per language).
  const buttonUrl = langButtonUrl || (ad?.button_url_pt || "").trim() || (ad?.button_url || "").trim();
  const enabled = ad?.enabled && !!imageUrl;
  const [visible, setVisible] = useState(() => (Number(ad?.delay_ms) || 0) === 0);

  // Cycle scheduler — respects `delay_ms`, `visible_ms`, `cycle_ms`.
  useEffect(() => {
    if (!enabled) return;
    const delay = Math.max(0, Number(ad.delay_ms) || 0);
    const visibleMs = Math.max(500, Number(ad.visible_ms) || 5000);
    const cycleMs = Math.max(0, Number(ad.cycle_ms) || 0);
    const hiddenMs = cycleMs > 0 ? Math.max(500, cycleMs - visibleMs) : 0;
    let cancelled = false;
    let timerId = null;
    const showThenLoop = () => {
      if (cancelled) return;
      setVisible(true);
      if (hiddenMs === 0) return; // always visible
      timerId = setTimeout(() => {
        if (cancelled) return;
        setVisible(false);
        timerId = setTimeout(showThenLoop, hiddenMs);
      }, visibleMs);
    };
    if (delay === 0) showThenLoop();
    else {
      setVisible(false);
      timerId = setTimeout(showThenLoop, delay);
    }
    return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
  }, [enabled, ad?.delay_ms, ad?.visible_ms, ad?.cycle_ms]);

  const buttonLabel = useMemo(() => {
    return ad?.[`button_label_${lang}`] || ad?.button_label_pt || "";
  }, [ad, lang]);
  const headerLabel = useMemo(() => {
    return ad?.[`header_${lang}`] || ad?.header_pt || "";
  }, [ad, lang]);

  if (!enabled) return null;

  const isLeft = side === "left";
  const anim = ANIM_MAP[ad.animation] || "";
  const btnShadow = ad.button_shadow
    ? "0 4px 0 rgba(0,0,0,0.85), 0 6px 14px rgba(0,0,0,0.25)"
    : "none";
  const btnStyle = {
    backgroundColor: ad.button_bg_color || "#FFEB3B",
    color: ad.button_text_color || "#000000",
    border: `2px solid ${ad.button_border_color || "#000000"}`,
    borderRadius: `${Number(ad.button_radius ?? 999)}px`,
    boxShadow: btnShadow,
    fontWeight: 900,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    lineHeight: 1,
    display: "inline-block",
    textDecoration: "none",
  };

  // Header follows the same primitive as the button — same knobs, own
  // colours/radius/shadow/size. Defaults keep the current dark pill look.
  const hdrShadow = ad.header_shadow
    ? "0 4px 0 rgba(0,0,0,0.85), 0 6px 14px rgba(0,0,0,0.25)"
    : "none";
  const hdrStyle = {
    backgroundColor: ad.header_bg_color || "#000000",
    color: ad.header_text_color || "#FFFFFF",
    border: `2px solid ${ad.header_border_color || "#000000"}`,
    borderRadius: `${Number(ad.header_radius ?? 999)}px`,
    boxShadow: hdrShadow,
    fontWeight: 900,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    lineHeight: 1,
    display: "inline-block",
  };

  const positionStyle = {
    top: `${Number(ad.top_offset) || 120}px`,
    [isLeft ? "left" : "right"]: "16px",
    width: `${Number(ad.width) || 160}px`,
    zIndex: Number(ad.z_index) || 40,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? "auto" : "none",
    transition: "opacity 400ms ease",
    gap: 0,
  };

  // Perspective 3D — applied to the whole ad wrapper. Only enabled when
  // `perspective_depth > 0`. Combines a `perspective(Npx)` with three
  // `rotate{X,Y,Z}` transforms so the admin has full control.
  const depth = Number(ad.perspective_depth) || 0;
  const rx = Number(ad.rotate_x) || 0;
  const ry = Number(ad.rotate_y) || 0;
  const rz = Number(ad.rotate_z) || 0;
  if (depth > 0 || rx || ry || rz) {
    const parts = [];
    if (depth > 0) parts.push(`perspective(${depth}px)`);
    if (rx) parts.push(`rotateX(${rx}deg)`);
    if (ry) parts.push(`rotateY(${ry}deg)`);
    if (rz) parts.push(`rotateZ(${rz}deg)`);
    positionStyle.transform = parts.join(" ");
    positionStyle.transformStyle = "preserve-3d";
  }

  const imgStyle = {
    animation: anim || "none",
    filter: "drop-shadow(0 8px 22px rgba(0,0,0,0.35))",
    marginTop: headerLabel ? `${Number(ad.header_gap ?? 8)}px` : 0,
  };

  const isExternal = /^https?:\/\//i.test(buttonUrl);

  return (
    <div
      className="eud-side-ad fixed hidden lg:flex flex-col items-center"
      style={positionStyle}
      data-testid={`side-ad-${side}`}
    >
      {headerLabel && (
        <span
          className={SIZE_CLASS[ad.header_size] || SIZE_CLASS.sm}
          style={hdrStyle}
        >
          {headerLabel}
        </span>
      )}
      <img
        src={imageUrl}
        alt=""
        className="w-full h-auto object-contain"
        style={imgStyle}
        loading="lazy"
        decoding="async"
      />
      {buttonLabel && buttonUrl && (
        <a
          href={buttonUrl}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className={`${SIZE_CLASS[ad.button_size] || SIZE_CLASS.md} hover:scale-105 transition-transform`}
          style={{ ...btnStyle, marginTop: `${Number(ad.button_gap ?? 8)}px` }}
        >
          {buttonLabel}
        </a>
      )}
    </div>
  );
}
